<template>
  <AppLayout>
    <div class="mb-6 flex items-center gap-4 flex-wrap">
      <Button
        v-tooltip.bottom="'List view options'"
        variant="text"
        severity="secondary"
        icon="pi pi-sliders-h"
        rounded
        aria-label="List view options"
        @click="adjustPopover.toggle($event)"
      />

      <Button
        v-tooltip.bottom="attentionFilter === 'true' ? 'Show all' : 'Show attention only'"
        icon="pi pi-exclamation-triangle"
        variant="text"
        rounded
        :severity="attentionFilter === 'true' ? 'danger' : 'secondary'"
        aria-label="Attention only"
        @click="attentionFilter = attentionFilter === 'true' ? 'false' : 'true'"
      />

      <div class="ml-auto flex items-center">
        <Button
          variant="text"
          severity="secondary"
          icon="pi pi-ellipsis-v"
          rounded
          aria-label="More actions"
          @click="actionsMenu?.toggle($event)"
        />
        <Menu ref="actionsMenu" :model="actionsMenuItems" :popup="true" />
      </div>
    </div>

    <Popover ref="adjustPopover" class="product-list-adjust-popover">
      <div class="flex flex-col gap-4 min-w-56">
        <div class="hidden md:block">
          <label class="text-sm font-medium text-surface-700 dark:text-surface-300 block mb-2">
            Image size
          </label>
          <Slider v-model="listImageSize" :min="50" :max="200" :step="5" class="w-full" />
          <div class="text-xs text-surface-500 mt-1">{{ listImageSize }}%</div>
        </div>
        <div>
          <label class="text-sm font-medium text-surface-700 dark:text-surface-300 block mb-2">
            View
          </label>
          <SelectButton
            v-model="viewType"
            :options="viewTypeOptions"
            optionLabel="label"
            optionValue="value"
          />
        </div>
        <div class="flex items-center justify-between gap-2">
          <label
            for="group-by-date-toggle"
            class="text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Group by date
          </label>
          <ToggleSwitch id="group-by-date-toggle" v-model="groupByDate" />
        </div>
      </div>
    </Popover>

    <template v-for="section in displaySections" :key="section.date ?? 'all'">
      <h2 v-if="section.date" class="mb-4">{{ section.date }}</h2>

      <div v-if="section.products.length === 0" class="text-surface-500 mb-4">(No products)</div>

      <!-- Grid view -->
      <div
        v-else-if="viewType === 'grid'"
        class="flex flex-row flex-wrap gap-2 mb-4 product-list-grid"
        :style="{
          '--list-image-size': `${listImageSizePx}px`,
        }"
      >
        <div
          v-for="product in section.products"
          :key="product.id"
          class="product-card-wrapper group list-card"
        >
          <div class="overflow-hidden">
            <RouterLink :to="`/products/${product.id}`" class="no-underline block">
              <div
                class="aspect-square overflow-hidden rounded-lg p-2 md:p-3 flex items-center justify-center box-border"
                :class="product.attentionCount > 0 ? 'bg-red-500/40' : 'bg-surface-800'"
              >
                <img
                  :src="getImageUrl(product.imageUrl, product.imageContentType, listImageSizePx)"
                  loading="lazy"
                  class="max-w-full max-h-full w-auto h-auto object-contain align-middle"
                />
              </div>
            </RouterLink>
            <div class="px-2 md:pt-1.5 flex items-center justify-between gap-1 min-w-0">
              <span
                class="flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400 shrink-0"
              >
                <i class="pi pi-images" />
                {{ product.imageCount }}
              </span>
              <div class="flex items-center shrink-0">
                <Button
                  @click.prevent="confirmDeleteProduct(product)"
                  icon="pi pi-trash"
                  text
                  rounded
                  size="small"
                  class="transition-opacity opacity-0 group-hover:opacity-100 text-surface-400 dark:text-surface-500"
                  severity="secondary"
                />
                <Button
                  @click.prevent="toggleAttention(product)"
                  icon="pi pi-exclamation-triangle"
                  text
                  rounded
                  size="small"
                  class="transition-opacity"
                  :class="{
                    'text-red-500': product.attentionCount > 0,
                    'text-surface-400 dark:text-surface-500 opacity-0 group-hover:opacity-100':
                      product.attentionCount === 0,
                  }"
                  :severity="product.attentionCount > 0 ? 'danger' : 'secondary'"
                />
              </div>
            </div>
            <RouterLink :to="`/products/${product.id}`" class="no-underline block px-2 pb-2">
              <div
                class="text-sm text-surface-900 dark:text-surface-0 truncate"
                :title="product.name || product.code || ''"
              >
                {{ product.name || product.code || '\u00A0' }}
              </div>
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- Table view -->
      <div v-else class="mb-4 overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-surface-200 dark:border-surface-700">
              <th
                class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400 w-0"
              >
                Image
              </th>
              <th class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400">
                Name
              </th>
              <th class="text-left py-2 pr-2 font-medium text-surface-600 dark:text-surface-400">
                Photos
              </th>
              <th class="w-10 py-2"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="product in section.products"
              :key="product.id"
              class="border-b border-surface-100 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800/50"
              :class="{
                'bg-red-50/50 dark:bg-red-950/20 border-l-2 border-l-red-400/60':
                  product.attentionCount > 0,
              }"
            >
              <td class="py-2 pr-2 align-middle">
                <RouterLink :to="`/products/${product.id}`" class="block w-fit">
                  <div
                    class="overflow-hidden rounded-lg list-card-image aspect-square shrink-0"
                    :style="{ width: `${listImageSizePx}px`, height: `${listImageSizePx}px` }"
                  >
                    <img
                      :src="
                        getImageUrl(product.imageUrl, product.imageContentType, listImageSizePx)
                      "
                      loading="lazy"
                      class="w-full h-full object-contain rounded-lg"
                    />
                  </div>
                </RouterLink>
              </td>
              <td class="py-2 pr-2 align-middle">
                <RouterLink
                  :to="`/products/${product.id}`"
                  class="text-surface-900 dark:text-surface-0 hover:underline no-underline"
                >
                  {{ product.name || product.code || '—' }}
                </RouterLink>
              </td>
              <td class="py-2 pr-2 align-middle text-surface-500 dark:text-surface-400">
                <span class="flex items-center gap-1">
                  <i class="pi pi-images" />
                  {{ product.imageCount }}
                </span>
              </td>
              <td class="py-2 align-middle">
                <div class="flex items-center">
                  <Button
                    @click.prevent="toggleAttention(product)"
                    icon="pi pi-exclamation-triangle"
                    text
                    rounded
                    size="small"
                    :class="{
                      'text-red-500': product.attentionCount > 0,
                      'text-surface-400 dark:text-surface-500': product.attentionCount === 0,
                    }"
                    :severity="product.attentionCount > 0 ? 'danger' : 'secondary'"
                  />
                  <Button
                    @click.prevent="confirmDeleteProduct(product)"
                    icon="pi pi-trash"
                    text
                    rounded
                    size="small"
                    class="text-surface-400 dark:text-surface-500"
                    severity="secondary"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div class="flex justify-start my-9">
      <Button variant="outlined" @click="loadProducts">Load more</Button>
    </div>

    <UploadImagesDialog v-model:visible="uploadFixedImagesDialogVisible" />
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import AppLayout from '@/components/AppLayout.vue';
import dayjs from 'dayjs';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import Popover from 'primevue/popover';
import SelectButton from 'primevue/selectbutton';
import Slider from 'primevue/slider';
import ToggleSwitch from 'primevue/toggleswitch';
import { useConfirm } from 'primevue/useconfirm';
import type { ListProductsEntryDto } from '@/api/generated';
import { groupBy } from 'lodash';
import { useApiClient } from '@/api/apiClient';
import { useAuth } from '@/modules/auth/auth';
import UploadImagesDialog from '@/modules/products/upload-images-dialog/UploadImagesDialog.vue';

const { api } = useApiClient();
const auth = useAuth();
const confirm = useConfirm();

const loading = ref(true);
const error = ref('');
const products = ref<ListProductsEntryDto[]>([]);

const attentionFilter = ref<string>('false');

const uploadFixedImagesDialogVisible = ref(false);

const adjustPopover = ref();
const actionsMenu = ref<InstanceType<typeof Menu>>();

const actionsMenuItems = computed((): MenuItem[] => [
  ...(auth.user.value?.superAdmin
    ? [
        {
          label: 'Download Attention Images',
          icon: 'pi pi-download',
          command: () =>
            window.open('/api/images/download-attention-images', '_blank', 'noopener,noreferrer'),
        },
      ]
    : []),
  {
    label: 'Upload Fixed Images',
    icon: 'pi pi-upload',
    command: () => (uploadFixedImagesDialogVisible.value = true),
  },
]);

const listImageSize = ref(Number(localStorage.getItem('productListImageSize')) || 100); // 100% = 150px, range 50–200%
watch(listImageSize, val => localStorage.setItem('productListImageSize', String(val)));
const listImageSizePx = computed(() => Math.round((listImageSize.value / 100) * 230));
const viewTypeOptions = [
  { label: 'Grid', value: 'grid' },
  { label: 'Table', value: 'table' },
];
const viewType = ref<'grid' | 'table'>('grid');
const groupByDate = ref(true);

// Watch for filter changes and reset products
watch(attentionFilter, () => {
  products.value = [];
  loadProducts();
});

async function loadProducts() {
  try {
    loading.value = true;
    const { data, error } = await api.productControllerList({
      query: {
        offset: products.value.length,
        limit: 50,
        attention: attentionFilter.value,
      },
    });
    if (error) {
      throw new Error('Failed to load products');
    }
    products.value = products.value.concat(data?.data ?? []);
    loading.value = false;
  } catch (err) {
    error.value = 'Failed to load products';
    throw err;
  } finally {
    loading.value = false;
  }
}

const productsByDay = computed(() => {
  const res = groupBy(products.value, p => dayjs(p.created).format('YYYY-MM-DD'));
  const entries = Object.entries(res).map(([date, products]) => ({
    date,
    products,
  }));
  return entries;
});

const displaySections = computed(() => {
  if (groupByDate.value) {
    return productsByDay.value;
  }
  return [{ date: null as string | null, products: products.value }];
});

loadProducts();

async function toggleAttention(product: ListProductsEntryDto) {
  const attention = product.attentionCount === 0;
  const { error } = await api.productControllerSetAttention({
    path: {
      productId: product.id,
    },
    body: {
      attention,
    },
  });
  if (error) {
    throw new Error('Failed to set attention');
  }
  product.attentionCount = attention ? 1 : 0;
}

function confirmDeleteProduct(product: ListProductsEntryDto) {
  confirm.require({
    message: `Are you sure you want to delete "${product.name || product.code}"?`,
    header: 'Delete Product',
    icon: 'pi pi-trash',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteProduct(product),
  });
}

async function deleteProduct(product: ListProductsEntryDto) {
  const { error } = await api.productControllerDelete({
    path: {
      productId: product.id,
    },
  });
  if (error) {
    throw new Error('Failed to delete product');
  }
  products.value = products.value.filter(p => p.id !== product.id);
}

function getImageUrl(imageUrl: string, contentType: string, width: number): string | undefined {
  const widthPx = devicePixelRatio * width;
  let url = `${imageUrl}=s${widthPx}`;
  if (contentType === 'image/webp') {
    url += '-rw';
  }
  return url;
}

// async function onProductCreated() {
//   // TODO: how to reset dialog state?
//   reset.value = true;
//   nextTick(() => {
//     reset.value = false;
//   });
// }
</script>

<style scoped>
.product-list-grid .list-card {
  width: var(--list-image-size);
  min-width: var(--list-image-size);
}

/* Mobile: at least 3 per row; respect image size slider (smaller size = more columns) */
@media (max-width: 767px) {
  .product-list-grid .list-card {
    width: min(var(--list-image-size), calc((100% - 1rem) / 3));
    min-width: min(var(--list-image-size), calc((100% - 1rem) / 3));
  }
}
</style>
